(() => {
    // The section can be added several times on a page and each instance
    // includes this script — only the first copy registers the handler.
    if (window.__dynamicSquareLoaded) return;
    window.__dynamicSquareLoaded = true;

    const debounce = (fn, wait) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), wait);
        };
    };

    class SquareCollections {
        constructor(section, options = {}) {
            this.id = section.id;
            this.container = section.el;
            this.settings = section.data || {};

            this.cellSelector = this.settings.cellSelector || options.cellSelector || '.item_content';

            // The carousel is the cells' direct parent (.square-wrapper).
            this.carousel = this.container.querySelector(this.settings.container || '.square-wrapper');

            this.flickity = null;
            this._lastWidth = this.container.clientWidth;

            // Stable handler refs so they can be removed on section unload.
            this._onResize = debounce(() => {
                // Only width matters; Flickity changing the height must not loop.
                const width = this.container.clientWidth;
                if (width === this._lastWidth) return;
                this._lastWidth = width;
                this._rebuild();
            }, 150);

            // Flickity locks the viewport height when built; lazy images (and
            // web fonts) loading afterwards would get clipped. Re-measure.
            this._onMediaLoad = debounce(() => this._resize(), 50);

            // ResizeObserver catches layout changes window 'resize' misses.
            if ('ResizeObserver' in window) {
                this._observer = new ResizeObserver(this._onResize);
                this._observer.observe(this.container);
            } else {
                window.addEventListener('resize', this._onResize);
            }
            this.container.addEventListener('load', this._onMediaLoad, true);
            document.fonts?.ready.then(this._onMediaLoad);

            this._setup();
        }

        _cells() {
            return [...this.carousel.querySelectorAll(this.cellSelector)];
        }

        _shouldEnableFlickity() {
            // offsetParent is null when the element (or an ancestor) is display:none.
            if (!this.carousel || this.carousel.offsetParent === null) return false;

            const cells = this._cells();
            if (cells.length < 2) return false;

            // Grid layout (1 column on mobile, 2 on desktop): turn into a
            // carousel once the cards stack into more than 2 rows.
            const rows = new Set(cells.map((cell) => Math.round(cell.offsetTop)));
            return rows.size > 2;
        }

        _setup() {
            if (!this.carousel || this.flickity || !this._shouldEnableFlickity()) return;

            if (typeof window.js_default !== 'function') {
                console.warn('dynamic-square: Flickity (window.js_default) not available yet.');
                return;
            }

            const Flickity = window.js_default();
            this.flickity = new Flickity(this.carousel, {
                accessibility: false,
                adaptiveHeight: false,
                cellSelector: this.cellSelector,
                contain: true,
                cellAlign: 'center',
                friction: 0.25,
                freeScroll: true,
                initialIndex: 0,
                lazyLoad: false,
                percentPosition: false,
                prevNextButtons: !!this.settings.allow_arrows,
                pageDots: !!this.settings.allow_dots
            });

            // Re-measure once the .flickity-enabled styles have applied.
            requestAnimationFrame(() => this._resize());
        }

        // Width changed: go back to the plain grid, re-check, rebuild if needed.
        // Measuring the grid itself is the only reliable way to know the layout.
        _rebuild() {
            this._destroy();
            this._setup();
        }

        _resize() {
            if (!this.flickity) return;
            this._equalizeHeights();
            this.flickity.resize();
        }

        // Carousel cells are absolutely positioned, so CSS can't stretch them
        // to a common height — measure the tallest card and apply it to all.
        _equalizeHeights() {
            const cells = this._cells();
            cells.forEach((cell) => { cell.style.minHeight = ''; });
            const tallest = Math.max(...cells.map((cell) => cell.offsetHeight));
            cells.forEach((cell) => { cell.style.minHeight = `${tallest}px`; });
        }

        _destroy() {
            if (!this.flickity) return;
            this.flickity.destroy();
            this.flickity = null;
            this._cells().forEach((cell) => { cell.style.minHeight = ''; });
        }

        // Called when the section is removed (theme editor).
        onSectionUnload() {
            this._observer?.disconnect();
            window.removeEventListener('resize', this._onResize);
            this.container.removeEventListener('load', this._onMediaLoad, true);
            this._destroy();
        }
    }

    // Self-contained instancing — no dependency on window.Sections (the
    // minified empire bundle doesn't expose it as a usable constructor).
    const SECTION_TYPE = 'dynamic-square';
    const instances = new Map();

    const parseData = (script) => {
        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.warn(`dynamic-square: invalid section data. ${error.message}`);
            return {};
        }
    };

    const mount = (root = document) => {
        root.querySelectorAll(`[data-section-type="${SECTION_TYPE}"][data-section-data]`).forEach((script) => {
            const id = script.dataset.sectionId;
            if (instances.has(id)) return;

            const el = script.closest('.shopify-section') || script.parentElement;
            instances.set(id, new SquareCollections({ id, el, data: parseData(script) }));
        });
    };

    const unmount = (id) => {
        instances.get(id)?.onSectionUnload();
        instances.delete(id);
    };

    // Theme editor: sections are re-rendered in place.
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
    document.addEventListener('shopify:section:unload', (event) => unmount(event.detail.sectionId));

    // Flickity (window.js_default) comes from empire.js, a module script that
    // runs after this deferred one — DOMContentLoaded fires once both ran.
    if (document.readyState === 'complete') {
        mount();
    } else {
        document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
    }
})();
